"use client";

import { CopyButton } from "@/components/copy-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { parseWalletSource } from "@/utils/portfolios/parse-portfolio-source";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { toast } from "sonner";

function formatAddress(address: string, startSlice = 8, endSlice = 4) {
  const start = address.slice(0, startSlice);
  const end = address.slice(-endSlice);
  const middle = ".".repeat(3);
  return `${start}${middle}${end}`;
}

export function PortfolioWalletInfo(props: {
  wallets: NonNullable<ReturnType<typeof parseWalletSource>>;
  variant: "full" | "compact";
  addressClassName?: string;
}) {
  const t = useTranslations("Portfolio.wallet.copy");
  const address = props.wallets[0].address;

  if (!address) {
    return;
  }

  const copyAddress = () => {
    if (typeof window === "undefined") {
      return;
    }

    navigator.clipboard.writeText(address);
    toast.success(t("toast"));
  };

  return (
    <div className="flex flex-wrap items-center">
      <span
        className={cn(
          "w-12 overflow-hidden text-ellipsis text-xs",
          props.addressClassName,
        )}
      >
        {formatAddress(address, props.variant === "full" ? 6 : 8)}
      </span>
      {props.variant === "full" ? (
        <>
          <CopyButton onCopy={copyAddress} className="size-6" />
          <div className="flex -space-x-2">
            {props.wallets.map((source) => {
              if (!source.src || !source.info) {
                return;
              }
              return (
                <span
                  key={source.chainId}
                  className="grid size-6 min-h-6 min-w-6 place-content-center rounded-full border bg-card transition-all duration-200 ease-in-out hover:scale-110"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Image
                          src={source.src}
                          alt={source.info.fullName}
                          className="size-4 rounded-full"
                          width={16}
                          height={16}
                          sizes="16px"
                        />
                      </TooltipTrigger>
                      <TooltipContent className="capitalize hover:scale-100">
                        {source.info.fullName}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
